import { Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, MenuItem, Select, Skeleton, TextField, Typography } from '@mui/material'
import React, { Fragment, useContext, useEffect, useState } from 'react'
import { toISODate } from 'src/utils/dateUtils'
import { useNotify, useShowController, useTranslate } from 'react-admin'
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LoadingButton } from '@mui/lab';
import { billingServices } from 'src/services/reservation/billing/billing.service'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap';
import { hasPermissionFunction } from 'src/helper/HasPermission';
import { userServices } from 'src/services/reservation/user/user.service';
import { PERMISSION_EDIT } from 'src/helper/Permission';
import EditIcon from '@mui/icons-material/Edit';
import { VisualizedInvoice } from '../logic/VisualizedInvoice';
import { useErrorHandler } from 'src/hooks/useErrorHandler'
import { ShowCard } from 'src/components/uiElements/cards/ShowCard';
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand';
import { ExpandMore } from '@mui/icons-material';

export const InvoiceItemsModal = ({
    openInvoice,
    setOpenInvoice,
    itemsSelected,
    setItemsSelected,
    minDueDate,
    onAfterClose,
}) => {
    const translate = useTranslate()
    const notify = useNotify()
    const showError = useErrorHandler()
    const { record } = useShowController()
    const [loadingPaymentCurrencies, setLoadingPaymentCurrencies] = useState(false)
    const [openExchangeModal, setOpenExchangeModal] = useState(false)
    const [loadingVisualize, setLoadingVisualize] = useState(false)
    const [triggerVisualize, setTriggerVisualize] = useState(false)
    const [timeDisabled, setTimeDisabled] = useState(false)
    const [currencies, setCurrencies] = useState([])
    // const [bases, setBases] = useState([])
    // const [loadingBases, setLoadingBases] = useState(false)
    const [invoiceLoading, setInvoiceLoading] = useState(false)
    const [isExpandedExchangeTypes, setIsExpandedExchangeTypes] = useState(false)

    const originalvalues = {
        reservation_id:record.id,
        item_id:itemsSelected,
        invoice_date: toISODate(),
        invoice_date_due: minDueDate ?? toISODate(),
        currency:null,
        exchanges:[],
        // base:null
    }

    const [form, setForm] = useState(originalvalues)

    // const getBases = async () =>{
    //     setLoadingBases(true)
    //     await billingServices.getBillingBases().then(res=>{
    //         const baseArray = Object.entries(res?.options ?? [])
    //         setBases(baseArray.map(base=>({label:base[0], value:base[1]})))
    //     }).finally(()=>{
    //         setLoadingBases(false)
    //     })
    // }

    useEffect(()=>{
        if(openInvoice){
            setForm({...originalvalues})
            // getBases()
            getCurrencyByUser()
            setIsExpandedExchangeTypes(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[openInvoice])

    useEffect(()=>{
        setTimeDisabled(true)
        setTimeout(()=>
             setTimeDisabled(false)
            ,5000)
    },[triggerVisualize])

    useEffect(()=>{
        setForm(prev=>({...prev, item_id:itemsSelected}))
    },[itemsSelected])

    const getCurrencyByUser = async () => {
        try {
            setLoadingPaymentCurrencies(true)
            const currencies = await userServices.getCurrencyByUser()
            if (currencies) {
                const format = currencies.filter((currency) => !currency?.is_reverse)

                setCurrencies(
                    currencies
                        .map(curr => curr?.currency_relation)
                        .filter((curr, index, self) => curr && self.findIndex(c => c?.id === curr?.id) === index)
                )

                setForm(prev=>({...prev, exchanges:format}))
            } else {
                return []
            }
        } catch (error) {
            return []
        } finally {
            setLoadingPaymentCurrencies(false)
        }
    }

    const onClose = () =>{
        onAfterClose?.()
        setOpenInvoice(false)
        setForm({...originalvalues, exchanges:form.exchanges})
    }

    const onSubmit = async () =>{
        setInvoiceLoading(true)
        await billingServices.invoiceItems(form).then(()=>{
            onClose()
            setItemsSelected([])
            notify('ra.notification.invoice_generated_success',{type:'success'})
        }).catch((e)=>{
            e.response.data.message = (e.response.data.data.errors && Array.isArray(e.response.data.data.errors)) ? e.response.data.data.errors[0] : e.response.data.data
            showError(e, () => notify('ra.notification.invoice_generated_error', { type: 'error' }))
            onAfterClose?.()
        }).finally(()=>{
            setInvoiceLoading(false)
            setOpenInvoice(false)
        })

    }

    const onChangeCurrency = (e) => {
        let currency = e.target.value
        if (currency) {
            delete currency.business_unit_relation
            delete currency.business_unit_to_relation
            delete currency.work_unit_relation
            delete currency.work_unit_to_relation
            setForm(prev=>({...prev, currency}))
        } else {
            setForm(prev=>({...prev, currency:{}}))
        }
    }

    const onCloseExhangeModal = () => {
        setOpenExchangeModal(false)
    }

    const handleCurrencyChange = (index, field, value) => {
        const updatedCurrencies = [...form.exchanges]
        updatedCurrencies[index] = { ...updatedCurrencies[index], [field]: parseFloat(value) }
        setForm(prev=>({...prev, exchanges:updatedCurrencies}))
    }

    return (
        <Dialog open={openInvoice} onClose={onClose} maxWidth={'sm'} fullWidth>
            <DialogTitle>
                {`${translate('ra.action.emit')} ${translate('ra.field.invoice')}`}
            </DialogTitle>

            <DialogContent sx={{ paddingRight: '9px' }}>
                <Grid container spacing={4}>
                    <EditInputsWrap className='small' label={translate(`ra.field.date`)}  md={6} lg={6} required>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='es'>
                            <DatePicker
                                value={form.invoice_date}
                                label={false}
                                onChange={(date) => {
                                    if (!date || !date.isValid()) return
                                    const onlydate = date.format('YYYY-MM-DD')
                                    setForm(prev=>({...prev, invoice_date: onlydate}))
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth variant='outlined' size='small' error={false} />
                                )}
                            />
                        </LocalizationProvider>
                    </EditInputsWrap>

                    <EditInputsWrap  className='small'label={translate(`ra.field.due_date`)}  md={6} lg={6} required>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='es'>
                            <DatePicker
                                value={form.invoice_date_due}
                                label={false}
                                onChange={(date) => {
                                    if (!date || !date.isValid()) return
                                    const onlydate = date.format('YYYY-MM-DD')
                                    setForm(prev=>({...prev, invoice_date_due: onlydate}))
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth variant='outlined' size='small' error={false} />
                                )}
                            />
                        </LocalizationProvider>
                    </EditInputsWrap>

                    <EditInputsWrap  className='small'label={translate(`ra.field.currency`)} md={6} lg={6} required>
                        {loadingPaymentCurrencies ?
                            <Skeleton variant='rectangular' width='100%' height={40} />
                        :
                            <Select
                                labelId='currencies'
                                id='currencies'
                                fullWidth
                                value={form.currency}
                                onChange={onChangeCurrency}
                                size='small'
                            >
                                {currencies.map((currency, i) => (
                                    <MenuItem key={i} value={currency}>
                                        {currency?.symbol} - {currency?.long_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        }
                    </EditInputsWrap>

                    {/* <EditInputsWrap  className='small'label={translate(`ra.field.billing_base`)} md={6} lg={6}>
                        {loadingBases ?
                            <Skeleton variant='rectangular' width='100%' height={40} />
                        :
                            <Select
                                labelId='bases'
                                id='bases'
                                fullWidth
                                value={form.base?.value}
                                onChange={(val)=>setForm(prev=>({...prev, base:{
                                    id:val.target.value,
                                    name:bases.find(base=>base.value === val.target.value)?.label
                                }}))}
                                size='small'
                            >
                                {bases.map((base, i) => (
                                    <MenuItem key={i} value={base.value}>
                                        {base?.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        }
                    </EditInputsWrap> */}

                    {form.exchanges.length > 0 && (
                        <Grid item xs={12} sx={{ paddingRight: '15px' }}>
                            <ShowCard
                                title={translate('ra.title.exchange_types')}
                                cardStyle={{ margin: 0 }}
                                preActionButtonJustify='flex-end'
                                preActionButton={
                                    <Box display='flex' alignItems='center' gap={1}>
                                        {hasPermissionFunction('currency-by-work-unit', PERMISSION_EDIT) && (
                                            <IconButton
                                                size="small"
                                                color='primary'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenExchangeModal(true);
                                                }}
                                                disabled={(form.currency && !Object.keys(form.currency)?.length) || !form.currency }
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            size='small'
                                            onClick={() => setIsExpandedExchangeTypes(prev => !prev)}
                                            style={{
                                                transform: isExpandedExchangeTypes ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s'
                                            }}
                                        >
                                            <ExpandMore />
                                        </IconButton>
                                    </Box>
                                }
                                headerStyle={isExpandedExchangeTypes ? {} : { borderRadius: 10 }}
                                contentStyle={{ padding: 0 }}
                            >
                                <Collapse in={isExpandedExchangeTypes}>
                                    <Grid container spacing={1} sx={{ padding: 4 }}>
                                        <Grid item xs={4} />

                                        <Grid item xs={4}><b>{translate('ra.field.exchange_b2')}</b></Grid>

                                        <Grid item xs={4}><b>{translate('ra.field.exchange_invoice')}</b></Grid>

                                        <Grid item xs={12} sx={{ padding: '4px 0' }}>
                                            <Divider />
                                        </Grid>

                                        {form.exchanges.map((currency, i) => (
                                            <Fragment key={i}>
                                                <Grid item xs={4}>
                                                    <Typography fontWeight='bold'>
                                                        {currency?.currency_relation?.short_name}-{currency?.currency_to_relation?.short_name}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={4}>
                                                    <Typography>{numberFormatterWithDecimalAndThousand(currency.value)}</Typography>
                                                </Grid>

                                                <Grid item xs={4}>
                                                    <Typography style={currency.billable_exchange_rate ? { color: '#52af52' } : {}}>
                                                        {numberFormatterWithDecimalAndThousand(currency.billable_exchange_rate)}
                                                    </Typography>
                                                </Grid>
                                            </Fragment>
                                        ))}
                                    </Grid>
                                </Collapse>
                            </ShowCard>
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ marginTop: '28px', paddingRight: '15px' }}>
                    <VisualizedInvoice
                        form={form}
                        triggerVisualize={triggerVisualize}
                        setLoadingVisualize={setLoadingVisualize}
                        setTriggerVisualize={setTriggerVisualize}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    variant='outlined'
                    color='error'
                    onClick={onClose}
                >
                    {translate('ra.action.cancel')}
                </Button>
                <LoadingButton
                    variant='contained'
                    onClick={()=>setTriggerVisualize(true)}
                    loading={loadingVisualize}
                    disabled={!form.currency || timeDisabled}
                >
                    {translate('ra.action.preview')}
                </LoadingButton>
                <LoadingButton
                    variant='contained'
                    onClick={onSubmit}
                    loading={invoiceLoading}
                >
                    {translate('ra.action.to_confirm')}
                </LoadingButton>
            </DialogActions>

            {openExchangeModal && (
                <Dialog fullWidth open={openExchangeModal} onClose={onCloseExhangeModal}>
                    <DialogTitle>{translate('ra.title.exchange_types')}</DialogTitle>

                    <DialogContent>
                        <Grid container spacing={3}>
                            <Grid item xs={4} />

                            <Grid item xs={4}>{translate('ra.field.exchange_b2')}</Grid>

                            <Grid item xs={4}>{translate('ra.field.exchange_invoice')}</Grid>

                            {form.exchanges.map((currency, i) => (
                                <Fragment key={i}>
                                    <Grid item xs={4}>
                                        <Typography component='span' fontWeight='bold'>
                                            {currency?.currency_relation?.short_name}-{currency?.currency_to_relation?.short_name}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={4}>
                                        <TextField
                                            defaultValue={currency.value}
                                            onChange={(e) => handleCurrencyChange(i, 'value', e.target.value)}
                                            size='small'
                                            type='number'
                                        />
                                    </Grid>

                                    <Grid item xs={4}>
                                        <TextField
                                            defaultValue={currency.billable_exchange_rate}
                                            onChange={(e) => handleCurrencyChange(i, 'billable_exchange_rate', e.target.value)}
                                            size='small'
                                            type='number'
                                        />
                                    </Grid>
                                </Fragment>
                            ))}
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button variant='outlined' onClick={onCloseExhangeModal}>
                            {translate('ra.action.close')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Dialog>
    )
}
